'use client';

import React, { useEffect, useRef, useState } from 'react';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import { ImageSize } from '@/ui/utils/getOptimizedImageUrl';
import { resizedImage } from '@/integrations/crystallize/helpers/crystallize-image-helper';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';

export interface GalleryImageSlideProps {
  image: ImageFragment;
  isActive: boolean;
}

export function GalleryImageSlide({ image, isActive }: Readonly<GalleryImageSlideProps>) {
  // Never reset to false on a src change: when the active slide swaps the placeholder for the
  // full image, the previous pixels stay painted until the new ones arrive, so no spinner is needed.
  const [isLoaded, setIsLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const size = isActive ? 1000 : 500;

  const markLoaded = () => setIsLoaded(true);

  // A cached image can finish loading before React attaches onLoad, so the event never reaches
  // us; read the element state directly on mount and whenever the src changes.
  useEffect(() => {
    const img = imageRef.current;

    if (img?.complete && img.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [image, isActive]);

  return (
    <div className="relative mx-auto h-full w-full">
      {/* The spinner shows until the image reports loaded or errored (an endless spinner is worse
          than a broken-image icon). It is still rendered BEHIND the image (z-0 vs z-10), so an
          opaque image covers it the moment it paints and a loaded image never flickers; removing
          it on load is what keeps it from showing through images with a transparent background.
          The original loads only for the selected slide; others use a small placeholder. */}
      {!isLoaded && (
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <div data-testid="main-media-spinner">
            <Spinner />
          </div>
        </div>
      )}
      <OptimizedImage
        ref={imageRef}
        image={isActive ? image : resizedImage(image, ImageSize.S500)}
        sizes={`${size}px`}
        width={size}
        height={size}
        onLoad={markLoaded}
        onError={markLoaded}
        className="relative z-10 mx-auto h-full w-full rounded-lg object-contain lg:py-5"
      />
    </div>
  );
}
